import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, HardDrive, Loader2, KeyRound, Copy, Lock, LogOut, AlertTriangle, ClipboardList, BarChart3 } from "lucide-react";
import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const REDIRECT_URI = `${API}/api/drive/callback`;
const TOKEN_KEY = "hp_admin_token";

const AdminDrive = () => {
  usePageMeta("Drive Connection | Heartland Physics", "Google Drive connection status.");
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [copied, setCopied] = useState(false);
  const [params] = useSearchParams();
  const justConnected = params.get("connected") === "1";
  const urlError = params.get("error");
  const URL_ERRORS = {
    exchange: "Google didn't complete the handoff. Click Connect Google Drive and try again in one smooth pass — if it fails a second time, contact support with the time it happened.",
    access_denied: "The Google authorization was cancelled. Click Connect Google Drive and approve the permission to finish connecting.",
    scope: "Google connected without the Drive permission. Click Connect Google Drive again and make sure to allow the Drive access checkbox.",
  };

  const handle401 = (res) => {
    if (res.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setAuthError("Session expired — please sign in again.");
      return true;
    }
    return false;
  };

  const authFetch = (url, opts = {}) =>
    fetch(url, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
    });

  const refresh = () =>
    authFetch(`${API}/api/drive/status`)
      .then(async (r) => {
        if (handle401(r)) return;
        setStatus(await r.json());
      })
      .catch(() => setStatus({ connected: false, configured: false }));

  useEffect(() => {
    if (token) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justConnected, token]);

  const signIn = async () => {
    if (!password) return;
    setSigningIn(true);
    setAuthError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Sign-in failed");
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (e) {
      setAuthError(e.message || "Sign-in failed");
    }
    setSigningIn(false);
  };

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStatus(null);
  };

  const saveConfig = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const res = await authFetch(`${API}/api/drive/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: clientId.trim(),
          client_secret: clientSecret.trim(),
          redirect_uri: REDIRECT_URI,
          frontend_origin: window.location.origin,
        }),
      });
      if (handle401(res)) return;
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      setClientId("");
      setClientSecret("");
      await refresh();
    } catch (e) {
      setSaveError(e.message || "Save failed");
    }
    setSaving(false);
  };

  const connect = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/drive/connect`);
      if (handle401(res)) return;
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "connect failed");
      window.location.href = data.authorization_url;
    } catch (e) {
      setLoading(false);
      alert("Could not start Google sign-in. Check that the Google credentials are configured.");
    }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(REDIRECT_URI).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch(`${API}/api/drive/test`, { method: "POST" });
      if (handle401(res)) return;
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Test failed");
      setTestResult({ ok: true, text: `Working — connected to ${data.account} and the "${data.folder}" folder is ready.` });
    } catch (e) {
      setTestResult({ ok: false, text: e.message || "Test failed" });
    }
    setTesting(false);
  };

  if (!token) {
    return (
      <section data-testid="admin-drive-page" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-md px-5 py-20 md:py-28">
          <Reveal y={12}>
            <div className="border border-border bg-white p-8" data-testid="admin-lock">
              <Lock size={22} className="text-blueprint" />
              <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-ink">Owner access</h1>
              <p className="mt-2 text-sm leading-relaxed text-ink/60">
                This page is private. Enter the owner password to manage the Google Drive connection.
              </p>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && signIn()}
                placeholder="Owner password"
                data-testid="admin-password-input"
                className="mt-6 w-full border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
              />
              {authError && (
                <p className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-auth-error">{authError}</p>
              )}
              <button
                onClick={signIn}
                disabled={signingIn || !password}
                data-testid="admin-login-button"
                className="mt-4 flex w-full items-center justify-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
              >
                {signingIn ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
                {signingIn ? "Checking…" : "Unlock"}
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="admin-drive-page" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-2xl px-5 py-20 md:px-8 md:py-28">
        <Reveal y={12}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Internal — Site owner</p>
              <h1 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Google Drive connection</h1>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Link to="/admin/requests" data-testid="admin-nav-requests" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <ClipboardList size={13} /> Requests
              </Link>
              <Link to="/admin/analytics" data-testid="admin-nav-analytics" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <BarChart3 size={13} /> Analytics
              </Link>
              <button
                onClick={signOut}
                data-testid="admin-logout-button"
                className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint"
              >
                <LogOut size={13} /> Lock
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-ink/60">
            Once connected, every shielding design request and every uploaded floor plan lands automatically in a "Heartland Physics Requests" folder in your Google Drive.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 border border-border bg-white p-7">
            {justConnected && status?.connected && (
              <p className="mb-5 flex items-center gap-2 border border-blueprint/30 bg-blueprint/5 px-4 py-3 text-sm font-medium text-ink" data-testid="drive-just-connected">
                <CheckCircle2 size={16} className="text-blueprint" /> Drive connected successfully.
              </p>
            )}
            {urlError && (
              <p className="mb-5 flex items-start gap-2 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="drive-url-error">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {URL_ERRORS[urlError] || "The Google connection did not complete — please try again."}
              </p>
            )}
            {status === null ? (
              <p className="flex items-center gap-2 text-sm text-ink/60"><Loader2 size={16} className="animate-spin" /> Checking connection…</p>
            ) : !status.configured ? (
              <div data-testid="drive-config-form">
                <div className="flex items-start gap-3" data-testid="drive-not-configured">
                  <KeyRound size={18} className="mt-0.5 text-blueprint" />
                  <div>
                    <p className="font-heading text-sm font-bold text-ink">Step 1 — Add your Google credentials</p>
                    <p className="mt-1 text-sm leading-relaxed text-ink/60">
                      In Google Cloud Console, create an OAuth client (Web application) with the Google Drive API enabled.
                      Add this as an <strong>Authorized redirect URI</strong>:
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 border border-border bg-blueprint/5 px-4 py-3">
                  <code className="flex-1 break-all font-mono text-xs text-ink/80" data-testid="drive-redirect-uri">{REDIRECT_URI}</code>
                  <button onClick={copyRedirect} data-testid="drive-copy-redirect" className="flex items-center gap-1 text-xs font-semibold text-blueprint hover:text-ink">
                    <Copy size={13} /> {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-ink/50">
                  Paste the credentials below. They are stored on the server only — never in the site's code.
                </p>
                <div className="mt-4 flex flex-col gap-3">
                  <input
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Google Client ID"
                    data-testid="drive-client-id"
                    className="w-full border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
                  />
                  <input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Google Client Secret"
                    data-testid="drive-client-secret"
                    className="w-full border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
                  />
                  {saveError && (
                    <p className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="drive-config-error">{saveError}</p>
                  )}
                  <button
                    onClick={saveConfig}
                    disabled={saving || !clientId.trim() || !clientSecret.trim()}
                    data-testid="drive-save-config"
                    className="flex items-center justify-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                    {saving ? "Saving…" : "Save credentials"}
                  </button>
                </div>
              </div>
            ) : status.connected ? (
              <div>
                <div className="flex items-center gap-3" data-testid="drive-connected">
                  <CheckCircle2 size={20} className="text-blueprint" />
                  <div>
                    <p className="font-heading text-sm font-bold text-ink">Connected</p>
                    <p className="mt-1 text-xs text-ink/55">Submissions and file uploads are syncing to your Google Drive.</p>
                  </div>
                </div>
                <div className="mt-5 border-t border-border pt-4">
                  <button
                    onClick={testConnection}
                    disabled={testing}
                    data-testid="drive-test-button"
                    className="flex items-center gap-2 border border-ink/25 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint disabled:opacity-60"
                  >
                    {testing ? <Loader2 size={15} className="animate-spin" /> : <HardDrive size={15} />}
                    {testing ? "Testing…" : "Test connection"}
                  </button>
                  {testResult && (
                    <p className={`mt-3 border px-4 py-3 text-sm ${testResult.ok ? "border-blueprint/30 bg-blueprint/5 text-ink" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="drive-test-result">
                      {testResult.text}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="mb-4 flex items-center gap-2 text-sm text-ink/60" data-testid="drive-config-saved">
                  <CheckCircle2 size={15} className="text-blueprint" /> Credentials saved. Step 2 — authorize your Google account.
                </p>
                <button
                  onClick={connect}
                  disabled={loading}
                  data-testid="drive-connect-button"
                  className="flex items-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <HardDrive size={16} />}
                  {loading ? "Opening Google sign-in…" : "Connect Google Drive"}
                </button>
                {status.redirect_uri && (
                  <p className="mt-4 break-all font-mono text-xs text-ink/40" data-testid="drive-redirect-reminder">
                    Redirect URI: {status.redirect_uri}
                  </p>
                )}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default AdminDrive;
